import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import CancelIcon from '@mui/icons-material/Cancel';
import BuildIcon from '@mui/icons-material/Build';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  CHECKLIST_ENABLED_PRODUCTS,
  ChecklistSummary
} from 'src/interfaces/ChecklistTypes';
import SetupProgress from '../Checklist/SetupProgress';
import useIsMobile from '../../../hooks/useIsMobile';

interface FlatInstallationViewProps {
  installations: I_Installation[];
}

const FlatInstallationView = (props: FlatInstallationViewProps) => {
  const intl = useIntl();
  // Phones (<md): render the installation list as stacked cards instead of a
  // wide table that clips columns. Desktop (>=md) keeps the exact table below.
  const isMobile = useIsMobile();
  const [isRowHovered, setHoveredRow] = useState(-1);
  const navigate = useNavigate();
  const [selectedInstallation, setSelectedInstallation] = useState<number>(-1);
  const currentLocation = useLocation();
  const { product, setProduct } = useContext(ProductIdContext);
  const BATCH_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByStatus, setSortByStatus] = useState('All Installations');
  const [sortByAction, setSortByAction] = useState('All Installations');
  const { currentUser } = useContext(UserContext);
  const showChecklistColumn =
    currentUser?.userType === UserType.admin &&
    CHECKLIST_ENABLED_PRODUCTS.has(product ?? -1);
  const [progressMap, setProgressMap] = useState<Map<number, ChecklistSummary>>(
    new Map()
  );

  useEffect(() => {
    if (!showChecklistColumn) return;
    axiosConfig
      .get('/GetChecklistSummary')
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const map = new Map<number, ChecklistSummary>();
        res.data.forEach((s: ChecklistSummary) => {
          map.set(s.installationId, s);
        });
        setProgressMap(map);
      })
      .catch(() => setProgressMap(new Map()));
  }, [showChecklistColumn, currentLocation.pathname]);

  const HoverableTableRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover // Or any other color
    }
  }));

  const indexedData = useMemo(() => {
    return props.installations.map((item) => ({
      ...item,
      nameLower: item.name.toLowerCase(),
      locationLower: item.location.toLowerCase(),
      regionLower: item.region.toLowerCase()
    }));
  }, [props.installations]);

  const sortedInstallations = useMemo(() => {
    let filtered = indexedData.filter(
      (item) =>
        item.nameLower.includes(searchTerm.toLowerCase()) ||
        item.locationLower.includes(searchTerm.toLowerCase()) ||
        item.regionLower.includes(searchTerm.toLowerCase())
    );

    // Apply the 'showOnly' filter
    switch (sortByStatus) {
      case 'Installations With Alarm':
        filtered = filtered.filter((i) => i.status === 2);
        break;
      case 'Installations with Warning':
        filtered = filtered.filter((i) => i.status === 1);
        break;
      case 'Functional Installations':
        filtered = filtered.filter((i) => i.status === 0);
        break;
      case 'Offline Installations':
        filtered = filtered.filter((i) => i.status === -1);
        break;
    }

    switch (sortByAction) {
      case 'Installations With Action Flag':
        filtered = filtered.filter((i) => i.testingMode === true);
        break;
      case 'Installations Without Action Flag':
        filtered = filtered.filter((i) => i.testingMode === false);
        break;
    }

    // Sort by status (alarms first); data-collection-disabled sinks below offline.
    return filtered.sort((a, b) => {
      const aDisabled = a.dataCollectionEnabled === false;
      const bDisabled = b.dataCollectionEnabled === false;
      if (aDisabled !== bDisabled) return aDisabled ? 1 : -1;

      const a_status = a.status;
      const b_status = b.status;

      if (a_status > b_status) return -1;
      if (a_status < b_status) return 1;
      return 0;
    });
  }, [searchTerm, indexedData, sortByAction, sortByStatus]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + BATCH_SIZE);
        }
      },
      {
        rootMargin: '600px', // triggers before the element fully enters view
        threshold: 0.5 // triggers when 10% of it is visible
      }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleSelectOneInstallation = (installationID: number): void => {
    if (selectedInstallation != installationID) {
      setSelectedInstallation(installationID);
      setSelectedInstallation(-1);

      navigate(
        product === 0
          ? routes.installations +
              routes.list +
              routes.installation +
              `${installationID}` +
              '/' +
              routes.live
          : routes.sodistore_installations +
              routes.list +
              routes.installation +
              `${installationID}` +
              '/' +
              routes.live,
        {
          replace: true
        }
      );
    } else {
      setSelectedInstallation(-1);
    }
  };

  const theme = useTheme();

  const handleRowMouseEnter = (id: number) => {
    setHoveredRow(id);
  };

  const handleRowMouseLeave = () => {
    setHoveredRow(-1);
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display:
              currentLocation.pathname === routes.installations + 'list' ||
              currentLocation.pathname === routes.installations + routes.list ||
              currentLocation.pathname ===
                routes.sodistore_installations + 'list' ||
              currentLocation.pathname ===
                routes.sodistore_installations + routes.list
                ? 'block'
                : 'none'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: '16px', // spacing between elements
              width: '100%'
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <TextField
                placeholder={intl.formatMessage({ id: 'search' })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchTwoToneIcon />
                    </InputAdornment>
                  )
                }}
              />
            </FormControl>

            <FormControl sx={{ width: isMobile ? '100%' : 240 }}>
              <InputLabel>
                <FormattedMessage
                  id="sortByStatus"
                  defaultMessage="Sort By Status"
                />
              </InputLabel>
              <Select
                value={sortByStatus}
                onChange={(e) => setSortByStatus(e.target.value)}
                label={intl.formatMessage({ id: 'showOnly' })}
              >
                {[
                  'All Installations',
                  'Installations With Alarm',
                  'Installations with Warning',
                  'Functional Installations',
                  'Offline Installations'
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ width: isMobile ? '100%' : 240 }}>
              <InputLabel>
                <FormattedMessage
                  id="sortByActionFlag"
                  defaultMessage="Sort By Action Flag"
                />
              </InputLabel>
              <Select
                value={sortByAction}
                onChange={(e) => setSortByAction(e.target.value)}
                label={intl.formatMessage({ id: 'showOnly' })}
              >
                {[
                  'All Installations',
                  'Installations With Action Flag',
                  'Installations Without Action Flag'
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      </Grid>

      <Grid container spacing={1} sx={{ marginTop: 0.1 }}>
        <Grid
          item
          xs={12}
          md="auto"
          sx={{
            display:
              currentLocation.pathname === routes.installations + 'list' ||
              currentLocation.pathname === routes.installations + routes.list ||
              currentLocation.pathname ===
                routes.sodistore_installations + 'list' ||
              currentLocation.pathname ===
                routes.sodistore_installations + routes.list
                ? 'block'
                : 'none'
          }}
        >
          {isMobile ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              {sortedInstallations.map((installation) => (
                <Card
                  key={installation.id}
                  onClick={() =>
                    handleSelectOneInstallation(installation.id)
                  }
                  sx={{ p: 2, cursor: 'pointer' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="text.primary"
                      sx={{ fontSize: 'small' }}
                    >
                      {installation.name}
                    </Typography>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flexShrink: 0
                      }}
                    >
                      {installation.testingMode && (
                        <BuildIcon
                          style={{
                            width: 18,
                            height: 18,
                            color: 'purple'
                          }}
                        />
                      )}
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor:
                            installation.status === 2
                              ? 'red'
                              : installation.status === 1
                              ? 'orange'
                              : installation.status === -1
                              ? 'red'
                              : installation.status === -2
                              ? '#f7b34d'
                              : 'green'
                        }}
                      />
                    </div>
                  </div>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textTransform: 'none' }}
                  >
                    {[
                      installation.location,
                      product === 4
                        ? installation.city
                        : installation.country
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                  {installation.orderNumbers && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textTransform: 'none' }}
                    >
                      {installation.orderNumbers}
                    </Typography>
                  )}
                  {showChecklistColumn && (
                    <div style={{ marginTop: 8 }}>
                      <SetupProgress
                        done={progressMap.get(installation.id)?.done ?? 0}
                        total={progressMap.get(installation.id)?.total ?? 13}
                      />
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id="name" defaultMessage="Name" />
                    </TableCell>
                    {product === 4 && (
                      <TableCell>
                        <FormattedMessage id="model" defaultMessage="Model" />
                      </TableCell>
                    )}
                    <TableCell>
                      <FormattedMessage
                        id="location"
                        defaultMessage="Location"
                      />
                    </TableCell>
                    {product !== 4 && (
                      <TableCell>
                        <FormattedMessage id="country" defaultMessage="Country" />
                      </TableCell>
                    )}
                    <TableCell>
                      <FormattedMessage
                        id="orderNumbers"
                        defaultMessage="Order Numbers"
                      />
                    </TableCell>
                    {product === 4 && (
                      <TableCell>
                        <FormattedMessage id="city" defaultMessage="City" />
                      </TableCell>
                    )}
                    <TableCell>
                      <FormattedMessage id="status" defaultMessage="Status" />
                    </TableCell>
                    {showChecklistColumn && (
                      <TableCell>
                        <FormattedMessage
                          id="setupProgress"
                          defaultMessage="Monitor Onboarding Progress"
                        />
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedInstallations.map((installation) => {
                    const isInstallationSelected =
                      installation.id === selectedInstallation;

                    const status = installation.status;
                    const rowStyles =
                      isRowHovered === installation.id
                        ? {
                            cursor: 'pointer',
                            backgroundColor: theme.colors.primary.lighter
                          }
                        : {};

                    return (
                      <TableRow
                        hover
                        key={installation.id}
                        selected={isInstallationSelected}
                        style={rowStyles}
                        onClick={() =>
                          handleSelectOneInstallation(installation.id)
                        }
                        onMouseEnter={() =>
                          handleRowMouseEnter(installation.id)
                        }
                        onMouseLeave={() => handleRowMouseLeave()}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.name}
                          </Typography>
                        </TableCell>

                        {product === 4 && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="text.primary"
                              gutterBottom
                              noWrap
                              sx={{ marginTop: '10px', fontSize: 'small' }}
                            >
                              {installation.installationModel || ''}
                            </Typography>
                          </TableCell>
                        )}

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.location}
                          </Typography>
                        </TableCell>

                        {product !== 4 && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="text.primary"
                              gutterBottom
                              noWrap
                              sx={{ marginTop: '10px', fontSize: 'small' }}
                            >
                              {installation.country}
                            </Typography>
                          </TableCell>
                        )}

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.orderNumbers}
                          </Typography>
                        </TableCell>

                        {product === 4 && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="text.primary"
                              gutterBottom
                              noWrap
                              sx={{ marginTop: '10px', fontSize: 'small' }}
                            >
                              {installation.city || ''}
                            </Typography>
                          </TableCell>
                        )}

                        <TableCell>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: '15px'
                            }}
                          >
                            {status === -1 ? (
                              <CancelIcon
                                style={{
                                  width: '23px',
                                  height: '23px',
                                  color: 'red',
                                  borderRadius: '50%'
                                }}
                              />
                            ) : (
                              ''
                            )}

                            {status === -2 ? (
                              <CircularProgress
                                size={20}
                                sx={{
                                  color: '#f7b34d'
                                }}
                              />
                            ) : (
                              ''
                            )}

                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                marginLeft: '2px',
                                borderRadius: '50%',
                                backgroundColor:
                                  status === 2
                                    ? 'red'
                                    : status === 1
                                    ? 'orange'
                                    : status === -1 || status === -2
                                    ? 'transparent'
                                    : 'green'
                              }}
                            />

                            {installation.testingMode && (
                              <BuildIcon
                                style={{
                                  width: '23px',
                                  height: '23px',
                                  color: 'purple',
                                  borderRadius: '50%',
                                  position: 'relative',
                                  zIndex: 1,
                                  marginLeft: '15px'
                                }}
                              />
                            )}
                          </div>
                        </TableCell>
                        {showChecklistColumn && (
                          <TableCell>
                            {(() => {
                              const summary = progressMap.get(installation.id);
                              return (
                                <SetupProgress
                                  done={summary?.done ?? 0}
                                  total={summary?.total ?? 13}
                                />
                              );
                            })()}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default FlatInstallationView;
